import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys, invoiceKeys } from '@/lib/queryKeys';
import type { CompanySettings, UpdateCompanyDto } from '@/types/settings';

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useApiMutation<CompanySettings, UpdateCompanyDto>({
    mutationFn: (dto) =>
      apiPatch<CompanySettings, UpdateCompanyDto>(
        operationsClient,
        settingsEndpoints.updateCompany(),
        dto,
      ),
    onSuccess: (data) => {
      // On-screen settings read `logoUrl` only, so write the fresh response
      // straight into the base cache for an instant preview update.
      queryClient.setQueryData(settingsKeys.company(), data);
      // NOT-236: the PDF flow caches a separate `includeLogoDataUri` payload
      // (inline logo data URI). `setQueryData` above is exact-match and never
      // touches it, so without this the next generated PDF would keep stale
      // branding until that key's own staleTime lapsed. Invalidate so it
      // refetches fresh on next PDF generation.
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.companyWithLogoDataUri(),
      });
      // Draft PDFs and invoice detail payloads carry resolved legal text.
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.details() });
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes('document-layout'),
      });
      toast.success('Company settings saved');
    },
  });
}
